import { ProviderSummary } from '../domain/types';
import { getProviderHealth } from './provider-health.utils';

export interface Verdict {
  total: number;
  current: number;
  /** "39 of 42 providers are current." */
  headline: string;
  /** Only the clauses that apply, e.g. ["3 failing", "1 inactive"]. */
  clauses: string[];
}

const pluralize = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural;

/**
 * The one-sentence state of the fleet at the top of the overview. Computed,
 * never a static string: clauses appear only when their count is non-zero.
 */
export const buildVerdict = (
  summaries: ProviderSummary[],
  now: Date = new Date(),
): Verdict => {
  const total = summaries.length;

  if (total === 0) {
    return { total: 0, current: 0, headline: 'No providers yet.', clauses: [] };
  }

  const healths = summaries.map(summary => getProviderHealth(summary, now));
  const current = healths.filter(health => health === 'current').length;
  const failing = healths.filter(health => health === 'failing').length;
  const inactive = healths.filter(health => health === 'inactive').length;
  const unconfigured = summaries.filter(
    summary => !summary.hasConfiguration,
  ).length;

  const clauses: string[] = [];

  if (failing > 0) {
    clauses.push(`${failing} failing`);
  }

  if (unconfigured > 0) {
    clauses.push(`${unconfigured} without a configuration`);
  }

  if (inactive > 0) {
    clauses.push(`${inactive} inactive`);
  }

  const noun = pluralize(total, 'provider', 'providers');
  // The subject of the sentence is the numerator — "1 of 2 providers is
  // current" — so the verb follows `current` while the noun follows `total`.
  const verb = pluralize(current, 'is', 'are');

  return {
    total,
    current,
    headline: `${current} of ${total} ${noun} ${verb} current.`,
    clauses,
  };
};
